import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { cpApi } from '../api/api';
import { useTransactionsFilters } from '../context/TransactionsFilterContext';
import { translateBalanceArticle } from '../utils/balanceArticleI18n';
import VndPageLoading from '../components/VndPageLoading';
import { formatCurrency } from '../utils/formatNumber';
import { intlLocaleFor } from '../i18n/config';

type Row = {
  id: number;
  transaction_type: string;
  article: string;
  amount: number;
  to_calc_balance_before: number;
  to_calc_balance_after: number;
  ref_type?: string;
  ref_id?: number;
  intake_id?: number;
  comment?: string | null;
  created_at: string;
  intake_amount_vnd?: number;
  intake_vnd_per_usdt?: number;
  intake_profile_name?: string;
  intake_profile_id?: number;
};

function formatTxDate(iso: string, locale: string, na: string): string {
  if (!iso) return na;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return na;
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

function rowTitle(r: Row, t: TFunction): string {
  const a = (r.article || '').trim();
  if (a) return translateBalanceArticle(a, t);
  const typ = (r.transaction_type || '').toLowerCase();
  if (typ === 'deposit') return t('balanceHistory.deposit');
  if (typ === 'withdrawal') return t('balanceHistory.withdrawal');
  return r.transaction_type || t('common.notAvailable');
}

export default function VndBalanceHistoryPage() {
  const { t, i18n } = useTranslation();
  const intlLocale = intlLocaleFor(i18n.language);
  const na = t('common.notAvailable');
  const txFilters = useTransactionsFilters();

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 25;

  const search = txFilters?.filters.search ?? '';
  const memberFilter = txFilters?.filters.memberFilter ?? 'all';
  const typeFilter = txFilters?.filters.typeFilter ?? 'all';
  const dateRange = txFilters?.filters.dateRange ?? { from: null, to: null };
  const dateFrom = dateRange.from
    ? `${dateRange.from.getFullYear()}-${String(dateRange.from.getMonth() + 1).padStart(2, '0')}-${String(dateRange.from.getDate()).padStart(2, '0')}`
    : '';
  const dateTo = dateRange.to
    ? `${dateRange.to.getFullYear()}-${String(dateRange.to.getMonth() + 1).padStart(2, '0')}-${String(dateRange.to.getDate()).padStart(2, '0')}`
    : '';

  const fetchPage = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      setError(null);
      try {
        const offset = (targetPage - 1) * limit;
        const parsedMember =
          memberFilter !== 'all' ? parseInt(String(memberFilter), 10) : NaN;
        const memberId =
          Number.isFinite(parsedMember) && parsedMember > 0 ? parsedMember : undefined;
        const res = await cpApi.getVndBalanceHistory({
          limit,
          offset,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          member_id: memberId,
          type: typeFilter !== 'all' ? typeFilter : undefined,
          search: search.trim() || undefined,
        });
        setRows(res.data as Row[]);
        setTotal(res.total);
        setPage(targetPage);
      } catch {
        setError(t('balanceHistory.loadError'));
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [limit, t, dateFrom, dateTo, memberFilter, typeFilter, search]
  );

  useEffect(() => {
    void fetchPage(1);
  }, [fetchPage]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  useEffect(() => {
    if (loading) return;
    window.dispatchEvent(
      new CustomEvent('paginationUpdate', {
        detail: { currentPage: page, totalPages, showPagination: true },
      })
    );
  }, [page, totalPages, loading]);

  useEffect(() => {
    const onPageChange = (e: Event) => {
      const p = (e as CustomEvent<{ page: number }>).detail?.page;
      if (typeof p === 'number' && p >= 1) void fetchPage(p);
    };
    window.addEventListener('pageChange', onPageChange);
    return () => window.removeEventListener('pageChange', onPageChange);
  }, [fetchPage]);

  useEffect(() => {
    return () => {
      window.dispatchEvent(
        new CustomEvent('paginationUpdate', {
          detail: { currentPage: 1, totalPages: 1, showPagination: false },
        })
      );
    };
  }, []);

  return (
    <div className="p-3 sm:p-4 md:p-6 space-y-4">
      {error && <div className="text-sm text-red-500">{error}</div>}

      <div className="rounded-xl border border-red-400/20 dark:border-red-500/25 bg-gradient-to-b from-red-500/[0.07] to-transparent dark:from-red-500/10 dark:to-transparent bg-white dark:bg-gray-800/90 shadow-sm overflow-hidden">
        {loading ? (
          <VndPageLoading fillTable />
        ) : rows.length === 0 ? (
          <div className="p-4 sm:p-6 text-sm text-gray-500 dark:text-gray-400">{t('balanceHistory.empty')}</div>
        ) : (
          <div className="p-3 sm:p-4 space-y-2">
            {rows.map((r) => {
              const isPositive = r.amount >= 0;
              const title = rowTitle(r, t);
              const intakeID = Number(r.intake_id || 0) > 0
                ? Number(r.intake_id || 0)
                : (r.ref_type === 'intake_request' && Number(r.ref_id || 0) > 0 ? Number(r.ref_id || 0) : 0);
              const intakeVnd = Number(r.intake_amount_vnd || 0) > 0 ? Number(r.intake_amount_vnd || 0) : null;
              const intakeRate = Number(r.intake_vnd_per_usdt || 0) > 0 ? Number(r.intake_vnd_per_usdt || 0) : null;
              const intakeProfileName = (r.intake_profile_name || '').trim();
              const intakeProfileId = Number(r.intake_profile_id || 0) > 0 ? Number(r.intake_profile_id || 0) : 0;
              return (
                <article
                  key={r.id}
                  className="rounded-xl border border-red-200/60 bg-white/95 p-4 shadow-sm dark:border-gray-700/70 dark:bg-gray-900/60"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 tabular-nums">#{r.id}</span>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                            isPositive
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/35 dark:text-emerald-200'
                              : 'bg-rose-100 text-rose-800 dark:bg-rose-900/35 dark:text-rose-200'
                          }`}
                        >
                          {isPositive ? t('balanceHistory.deposit') : t('balanceHistory.withdrawal')}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                          {formatTxDate(r.created_at, intlLocale, na)}
                        </span>
                      </div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</div>
                      {intakeID > 0 ? (
                        <div className="text-xs text-emerald-700 dark:text-emerald-300">
                          {t('balanceHistory.accrualRequestLabel', { id: intakeID })}
                        </div>
                      ) : null}
                      {intakeID > 0 && (intakeVnd != null || intakeRate != null || intakeProfileName || intakeProfileId > 0) ? (
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          {t('balanceHistory.intakeMetaLine', {
                            vnd: intakeVnd != null ? Math.round(intakeVnd).toLocaleString(intlLocale) : na,
                            rate:
                              intakeRate != null
                                ? intakeRate.toLocaleString(intlLocale, {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 2,
                                  })
                                : na,
                            profile:
                              intakeProfileName || (intakeProfileId > 0 ? `#${intakeProfileId}` : na),
                          })}
                        </div>
                      ) : null}
                      {r.comment ? (
                        <p className="text-xs text-gray-600 dark:text-gray-400 break-words leading-relaxed">{r.comment}</p>
                      ) : null}
                    </div>
                    <div className="shrink-0 text-left sm:text-right border-t border-red-100/80 dark:border-gray-700/80 pt-3 sm:border-0 sm:pt-0">
                      <div
                        className={`text-lg font-bold tabular-nums tracking-tight ${
                          isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
                        }`}
                      >
                        {isPositive ? '+' : '−'}
                        {formatCurrency(Math.abs(r.amount), 'USDT')}
                      </div>
                      <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
                        {t('balanceHistory.balanceBeforeAfter')}
                        <div className="tabular-nums text-gray-700 dark:text-gray-300 mt-0.5">
                          {formatCurrency(r.to_calc_balance_before, 'USDT')} → {formatCurrency(r.to_calc_balance_after, 'USDT')}
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
